import { Text } from "@fluentui/react-components";
import { useMediaQuery, useSessionStorage } from "usehooks-ts";

import { DESKTOP_MEDIA_QUERY } from "../lib/constants";
import TimesheetByProjectManagerTable from "../components/tables/timesheet-by-project-manager-table";
import { useSpinnerContext } from "../components/spinner/spinner-context";
import { useToast } from "../components/ui/toast/use-toast";
import { useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import {
  TimesheetByPMResponse,
  fromTimesheetByPMResponseToTimesheetByPM,
  getProjectNames,
  getRowsForFooter,
  getRowsForHeader,
  getUsersNames,
} from "../models/TimesheetByPM";
import { useTableWeekPickerContext } from "../components/week-picker/table-week-picker-context";
import { UserData } from "../models/UserData";
import { Command, Commands } from "../lib/command";
import TimesheetByPMFilter from "../components/filters/timesheet-by-pm-filter";
import { useFiltersContext } from "../components/filters/filters-context";
import api from "../lib/apiBase";
import Alert from "../components/alert/Alert";
import { SpinnerItem } from "../components/spinner/spinner";
import { SelectUserByAdmin, initSelectUserByAdmin } from "../components/select-users/select-users";
import { isApiError } from "../models/ApiError";
import { useSelectUserContext } from "../components/select-users/select-user-context";

const PAGE_FILE_NAME = "ApprovalsPM";

export default function TimesheetByProjectManager() {
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const [selectedUser] = useSessionStorage<SelectUserByAdmin>(
    "select-user-by-admin",
    initSelectUserByAdmin
  );
  const [user] = useSessionStorage<UserData>("user", {
    isPM: false,
    isR: false,
    isRM: false,
    isAdmin: false,
    resourceId: null,
    maxHoursPerDay: 0,
  });
  const resourceId = useMemo(() => {
    if (user.resourceId !== "" && user.resourceId !== null) {
      return selectedUser.resourceId ? selectedUser.resourceId : user.resourceId;
    }
  }, [selectedUser]);
  const { toast } = useToast();
  const { firstDay, lastDay } = useTableWeekPickerContext();
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (firstDay !== "" && lastDay !== "" && resourceId !== undefined) {
      setUrl(
        `/v1/Worklogs/pm-timesheets?projectManagerId=${resourceId}&dateFrom=${firstDay}&dateTo=${lastDay}`
      );
    }
  }, [firstDay, lastDay, resourceId]);

  const { url: selectUserUrl, refresh: selectUserRefresh } = useSelectUserContext();

  useEffect(() => {
    if (firstDay !== "" && lastDay !== "") {
      selectUserUrl.setUrl(
        `/v1/Resources/GetNotApprovingPMs?dateFrom=${firstDay}&dateTo=${lastDay}`
      );
    }
  }, [firstDay, lastDay]);

  const { onOpenToggle } = useSpinnerContext();

  const [response, refresh] = useApi<TimesheetByPMResponse[]>(url);

  const handleCommandTask = async (
    _resourceId: string,
    projectId: string,
    taskId: string,
    command: Command,
    commandDescripition: string
  ) => {


    onOpenToggle(true);
    try {
      await api(
        `/v1/Worklogs?adminResourceId=${user.resourceId}&resourceId=${_resourceId}&command=${command}&commandDescription=${commandDescripition}`,
        {
          method: "POST",
          data: response.data?.filter(
            (item) =>
              item.resourceId === _resourceId &&
              item.projectId === projectId &&
              item.taskId === taskId
          ),
        }
      );
      let commandMessage = "";
      if (command === Commands[0]) {
        commandMessage = "The Timesheet Task has been successfully approved.";
      } else if (command === Commands[5]) {
        commandMessage = "The Timesheet Task has been successfully rejected.";
      }
      toast({
        variant: "success",
        title: commandMessage,
      });
      selectUserRefresh.current?.();
      refresh();
    } catch (error) {
      if (isApiError(error)) {
        toast({
          variant: "error",
          title: error.message,
        });
      } else {
        // otkomentarisati kada se popravi backend
        //navigate("/default-error");
      }
    }

    onOpenToggle(false);
  };

  const handleCommandResource = async (_resourceId: string) => {
    onOpenToggle(true);

    await api(
      `/v1/Worklogs?adminResourceId=${user.resourceId}&resourceId=${resourceId}&command=approvepm`,
      {
        method: "POST",
        data: response.data?.filter((item) => item.resourceId === _resourceId),
      }
    );
    toast({
      variant: "success",
      title: "The Timesheet has been successfully approved.",
    });
    selectUserRefresh.current?.();
    refresh();
    onOpenToggle(false);
  };

  const modifyResponse = useMemo(
    () => fromTimesheetByPMResponseToTimesheetByPM(handleCommandTask, response.data),
    [response]
  );

  const optionsProjects = useMemo(() => getProjectNames(response.data), [response]);
  const optionsUsers = useMemo(() => getUsersNames(response.data), [response]);

  const [visibleProjects, setVisibleProjects] = useState(modifyResponse);

  useEffect(() => {
    setVisibleProjects(modifyResponse);
  }, [modifyResponse]);

  const { setFilters, setShowFilter } = useFiltersContext();

  useEffect(() => {
    if (modifyResponse.length === 0) {
      setShowFilter(false);
    } else {
      setShowFilter(true);
    }
    setFilters(
      <TimesheetByPMFilter
        modifyResponse={modifyResponse}
        setVisibleProjects={setVisibleProjects}
        optionsProjects={optionsProjects}
        optionsUsers={optionsUsers}
      />
    );
  }, [modifyResponse, optionsProjects, optionsUsers]);

  if (response.error) {
    return null;
  }
  if (!response.data) {
    return <SpinnerItem />;
  }

  if (response.data.length === 0) {
    return (
      <section className="pt-5 lg:px-8">
        <Alert
          id="alert-info"
          title="There are no pending worklogs in the approval queue awaiting review."
          status="Info"
        />
      </section>
    );
  }

  return (
    <div className="flex flex-col pt-5 gap-6">
      {isDesktop ? (
        <TimesheetByPMFilter
          modifyResponse={modifyResponse}
          setVisibleProjects={setVisibleProjects}
          optionsProjects={optionsProjects}
          optionsUsers={optionsUsers}
        />
      ) : null}

      <section className="lg:px-8">
        <TimesheetByProjectManagerTable
          data={visibleProjects}
          response={response.data}
          getRowsForHeader={getRowsForHeader}
          getRowsForFooter={getRowsForFooter}
          handleCommand={handleCommandResource}
        />
      </section>

      <section className="flex flex-col justify-between items-center mt-4 gap-4 lg:px-8 md:flex-row">
        <div className="flex flex-col gap-1 self-start md:self-auto">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#F4F99B]"></div>
            <Text>- Submitted</Text>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#C5E8A8]"></div>
            <Text>- Approved</Text>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#FBB5B5]"></div>
            <Text>- Rejected</Text>
          </div>
        </div>
      </section>
    </div>
  );
}
